/**
 * Deep-link handling for the native shell.
 *
 * On the web, Supabase's `detectSessionInUrl` reads auth callback params
 * (email verification, password reset, OAuth) straight off the initial page
 * URL. Inside the native app there is no page navigation for those links —
 * iOS hands the app a URL through the `appUrlOpen` event instead. This module
 * turns that event into an in-app router navigation, and forwards any auth
 * tokens/code in the URL to Supabase so the session is established.
 *
 * Register `initDeepLinks(navigate)` once, from a component that owns the
 * react-router `navigate` function (see AuthProvider).
 */

import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { supabase } from '@/lib/supabase';
import { isNativePlatform } from './platform';

let started = false;

/**
 * @param navigate  react-router navigate function; receives an in-app path
 *                  like `/verify-email?token=...`.
 */
export async function initDeepLinks(navigate: (path: string) => void): Promise<void> {
  if (!isNativePlatform() || started) return;
  started = true;

  await App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
    const raw = event.url;
    if (!raw) return;

    // Strip the scheme/host so we're left with an in-app path. Handles both
    // custom-scheme (com.madlan.projexhq://verify-email?...) and universal links
    // (https://app.projexhq.com/verify-email?...).
    const path = extractPath(raw);

    // If the link carries auth material (PKCE `code`, or an access/refresh
    // token pair in the hash), let Supabase establish the session first.
    await maybeCompleteAuth(raw);

    if (path) navigate(path);
  });
}

/** Reduce a full deep-link URL to a router path (`/x?y#z`). */
function extractPath(url: string): string {
  try {
    const u = new URL(url);
    return `${u.pathname}${u.search}${u.hash}` || '/';
  } catch {
    // Custom schemes like `com.madlan.projexhq://verify-email` don't always parse
    // as a standard URL; fall back to a manual split after the `://host`.
    const afterScheme = url.replace(/^[^:]+:\/\//, '');
    const slash = afterScheme.indexOf('/');
    return slash >= 0 ? afterScheme.slice(slash) : '/';
  }
}

/** If the URL contains Supabase auth params, exchange them for a session. */
async function maybeCompleteAuth(url: string): Promise<void> {
  if (!supabase) return;
  try {
    const u = new URL(url.replace(/^[^:]+:\/\//, 'https://placeholder/'));
    const code = u.searchParams.get('code');
    if (code) {
      await supabase.auth.exchangeCodeForSession(code);
      return;
    }
    // Implicit-flow tokens arrive in the hash fragment.
    const hash = new URLSearchParams(u.hash.replace(/^#/, ''));
    const access_token = hash.get('access_token');
    const refresh_token = hash.get('refresh_token');
    if (access_token && refresh_token) {
      await supabase.auth.setSession({ access_token, refresh_token });
    }
  } catch {
    /* not an auth link — ignore */
  }
}
